import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Card, Button, Table, Alert, Badge } from 'react-bootstrap';
import { Autocomplete, TextField, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { NumericFormat } from 'react-number-format';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../Component/Header';
import Menu from '../../Component/Menu';
import Footer from '../../Component/Footer';
import NewJournalService from '../NewJournalService';
import EquityService from '../Subhead/EquityService';
import Credentials, {
  currentTenant,
  financialyearstart,
  financialyearend,
  thousandsdisplay,
  currency,
  userid,
  getFinancialYearLabel
} from '../../Common/Credentials';

const PartnerCurrentEquity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fylist, setFylist] = useState([]);
  const [selectedFy, setSelectedFy] = useState(null);
  const [financialYearId, setFinancialYearId] = useState('');
  const [narration, setNarration] = useState('');
  const [partnerRows, setPartnerRows] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [existingRecordId, setExistingRecordId] = useState(null);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const curUnit = currency || 'QR.';

  useEffect(() => {
    const activeTenant = currentTenant || sessionStorage.getItem('tenant') || 'DEFAULT_TENANT';

    setLoading(true);
    // 1. Fetch Financial Years
    NewJournalService.getFinancialYears(activeTenant)
      .then((res) => {
        const list = res.data || [];
        const def = { from: financialyearstart, to: financialyearend };
        const rawList = list.concat(def);
        const uniqueList = [];
        const seen = new Set();
        rawList.forEach((item) => {
          if (!item || !item.from || !item.to) return;
          const lbl = getFinancialYearLabel(item.from, item.to);
          if (!seen.has(lbl)) {
            seen.add(lbl);
            uniqueList.push(item);
          }
        });
        setFylist(uniqueList);

        // Set default selected FY to current year
        const defLabel = getFinancialYearLabel(financialyearstart, financialyearend);
        const currentFy = uniqueList.find(f => getFinancialYearLabel(f.from, f.to) === defLabel) || (uniqueList.length > 0 ? uniqueList[0] : def);
        setSelectedFy(currentFy);
        handleFySelection(currentFy);
      })
      .catch((err) => {
        console.error('Error fetching financial years:', err);
      })
      .finally(() => setLoading(false));

    // 2. Fetch Master Partners list
    EquityService.getPartners(activeTenant)
      .then((res) => {
        const data = res && res.data ? res.data : [];
        const normalized = data.map((p) => ({
          id: p.id,
          name: p.name || p.label || String(p.id)
        }));
        setAvailablePartners(normalized);
      })
      .catch((err) => console.error('Error fetching partners:', err));
  }, []);

  const getFyId = (from, to, id) => {
    const lbl = getFinancialYearLabel(from, to);
    if (lbl) return lbl;
    return id ? String(id) : 'current';
  };

  const handleFySelection = async (fyOption) => {
    if (!fyOption) {
      setSelectedFy(null);
      setFinancialYearId('');
      setPartnerRows([]);
      setExistingRecordId(null);
      return;
    }

    setSelectedFy(fyOption);
    const fyId = getFyId(fyOption.from, fyOption.to, fyOption.id);
    setFinancialYearId(fyId);
    setSaveSuccessMessage('');
    setErrorMessage('');
    setLoading(true);

    const activeTenant = currentTenant || sessionStorage.getItem('tenant') || 'DEFAULT_TENANT';
    try {
      // Check for existing Partner Current record for this FY
      const res = await EquityService.getPartnerCurrentRecord(activeTenant, fyId);
      if (res.data && (res.data.id || res.data.totalAmount !== undefined)) {
        const rec = res.data;
        setExistingRecordId(rec.id);
        setNarration(rec.narration || '');

        let parsedDetails = [];
        try {
          parsedDetails = typeof rec.partnerDetails === 'string'
            ? JSON.parse(rec.partnerDetails)
            : rec.partnerDetails || [];
        } catch (e) {
          parsedDetails = [];
        }

        if (parsedDetails.length > 0) {
          setPartnerRows(parsedDetails);
          setLoading(false);
          return;
        }
      } else {
        setExistingRecordId(null);
        setNarration(`Partner current / owner equity for FY ${fyId}`);
      }

      // If no saved record exists, initialize from active share capital allocations or master partners
      const allocRes = await EquityService.getAllocatList(activeTenant).catch(() => ({ data: [] }));
      const allocData = allocRes && allocRes.data ? allocRes.data : [];

      if (Array.isArray(allocData) && allocData.length > 0) {
        // Group by partnerId and pick latest
        const partnerMap = new Map();
        allocData.forEach((item) => {
          const pid = item.partnerId || item.id;
          if (pid && (!item.status || String(item.status).toLowerCase() === 'active')) {
            partnerMap.set(String(pid), {
              partnerId: pid,
              partnerName: item.partnerLabel || item.name || `Partner (${pid})`,
              sharePercentage: Number(item.partnerPercentage || 0),
              openingBalance: 0,
              additions: 0,
              drawings: 0,
              closingAmount: 0,
              remarks: ''
            });
          }
        });

        if (partnerMap.size > 0) {
          setPartnerRows(Array.from(partnerMap.values()));
          setLoading(false);
          return;
        }
      }

      // Fallback: master partners
      if (availablePartners.length > 0) {
        const initial = availablePartners.map((p) => ({
          partnerId: p.id,
          partnerName: p.name,
          sharePercentage: 0,
          openingBalance: 0,
          additions: 0,
          drawings: 0,
          closingAmount: 0,
          remarks: ''
        }));
        setPartnerRows(initial);
      } else {
        setPartnerRows([
          {
            partnerId: `p-${Date.now()}`,
            partnerName: '',
            sharePercentage: 0,
            openingBalance: 0,
            additions: 0,
            drawings: 0,
            closingAmount: 0,
            remarks: ''
          }
        ]);
      }
    } catch (err) {
      console.error('Error loading partner current record:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowChange = (index, field, value) => {
    const updated = [...partnerRows];
    const row = { ...updated[index], [field]: value };

    // Auto-calculate closing amount if opening, additions, or drawings change
    if (field === 'openingBalance' || field === 'additions' || field === 'drawings') {
      const open = Number(field === 'openingBalance' ? value : row.openingBalance) || 0;
      const add = Number(field === 'additions' ? value : row.additions) || 0;
      const draw = Number(field === 'drawings' ? value : row.drawings) || 0;
      row.closingAmount = open + add - draw;
    }

    updated[index] = row;
    setPartnerRows(updated);
  };

  const handleAddPartnerRow = () => {
    setPartnerRows([
      ...partnerRows,
      {
        partnerId: `p-${Date.now()}`,
        partnerName: '',
        sharePercentage: 0,
        openingBalance: 0,
        additions: 0,
        drawings: 0,
        closingAmount: 0,
        remarks: ''
      }
    ]);
  };

  const handleRemovePartnerRow = (index) => {
    const updated = partnerRows.filter((_, i) => i !== index);
    setPartnerRows(updated);
  };

  const totalOwnerEquity = partnerRows.reduce(
    (sum, row) => sum + (Number(row.closingAmount) || 0),
    0
  );

  const totalAdditions = partnerRows.reduce(
    (sum, row) => sum + (Number(row.additions) || 0),
    0
  );

  const totalDrawings = partnerRows.reduce(
    (sum, row) => sum + (Number(row.drawings) || 0),
    0
  );

  const totalOpening = partnerRows.reduce(
    (sum, row) => sum + (Number(row.openingBalance) || 0),
    0
  );

  const handleSave = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSaveSuccessMessage('');

    if (!financialYearId) {
      setErrorMessage('Please select a financial year.');
      return;
    }

    if (partnerRows.length === 0) {
      setErrorMessage('Please add at least one partner entry.');
      return;
    }

    // Validate that partner rows have names
    for (let i = 0; i < partnerRows.length; i++) {
      if (!partnerRows[i].partnerName || !partnerRows[i].partnerName.trim()) {
        setErrorMessage(`Partner row ${i + 1} must have a valid partner name.`);
        return;
      }
    }

    const activeTenant = currentTenant || sessionStorage.getItem('tenant') || 'DEFAULT_TENANT';
    const payload = {
      tenantId: activeTenant,
      financialYearId: financialYearId,
      fromDate: selectedFy && selectedFy.from ? String(selectedFy.from) : null,
      toDate: selectedFy && selectedFy.to ? String(selectedFy.to) : null,
      totalAmount: totalOwnerEquity,
      narration: narration,
      createdBy: userid || 'admin',
      partners: partnerRows.map((p) => ({
        partnerId: p.partnerId || '',
        partnerName: p.partnerName.trim(),
        sharePercentage: Number(p.sharePercentage) || 0,
        openingBalance: Number(p.openingBalance) || 0,
        additions: Number(p.additions) || 0,
        drawings: Number(p.drawings) || 0,
        closingAmount: Number(p.closingAmount) || 0,
        remarks: p.remarks || ''
      }))
    };

    setLoading(true);
    try {
      const res = await EquityService.savePartnerCurrentRecord(payload);
      if (res.data && res.data.id) {
        setExistingRecordId(res.data.id);
      }
      setSaveSuccessMessage(
        `Partner current / owner equity of ${curUnit} ${totalOwnerEquity.toLocaleString()} saved successfully for FY ${financialYearId}! It will now reflect in the Balance Sheet.`
      );
    } catch (err) {
      console.error('Error saving partner current record:', err);
      setErrorMessage(
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Failed to save partner current record.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h4 className="font-weight-bold text-dark mb-0">
                  <AccountBalanceWalletIcon className="mr-2 text-primary" style={{ fontSize: 30 }} />
                  Partner Current / Owner Equity
                </h4>
                <small className="text-muted">
                  Add and maintain partner current account balances and owner equity per financial year
                </small>
              </div>
              <div className="col-sm-6 text-right">
                <Link to="/balancesheet" className="btn btn-outline-info btn-sm mr-2" title="Go to Balance Sheet">
                  <AssessmentIcon className="mr-1" style={{ fontSize: 18 }} /> View Balance Sheet
                </Link>
                <Link to="/accounts" className="btn btn-danger btn-sm text-white">
                  <i className="fas fa-times"></i> Close
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="content">
          <div className="container-fluid">
            {/* Year Selector Card */}
            <Card className="shadow-sm border-0 mb-3">
              <Card.Body className="p-3">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <label className="font-weight-bold mb-1">Select Financial Year:</label>
                    <Autocomplete
                      id="partner-fy-select"
                      options={fylist}
                      getOptionLabel={(option) => {
                        if (option && option.from && option.to) {
                          return getFinancialYearLabel(option.from, option.to);
                        }
                        return 'Current Year';
                      }}
                      value={selectedFy}
                      onChange={(e, val) => handleFySelection(val)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          variant="outlined"
                          placeholder="Select Financial Year"
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="font-weight-bold mb-1">Narration / Description:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={narration}
                      onChange={(e) => setNarration(e.target.value)}
                      placeholder="e.g. Partner current adjustments for the financial year"
                    />
                  </div>
                  <div className="col-md-3 text-right pt-3">
                    {existingRecordId ? (
                      <Badge bg="success" className="p-2" style={{ fontSize: '13px' }}>
                        <CheckCircleIcon style={{ fontSize: 16 }} className="mr-1" />
                        Record Exists for FY {financialYearId}
                      </Badge>
                    ) : (
                      <Badge bg="secondary" className="p-2" style={{ fontSize: '13px' }}>
                        New Entry for FY {financialYearId || 'None'}
                      </Badge>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Status Messages */}
            {saveSuccessMessage && (
              <Alert variant="success" dismissible onClose={() => setSaveSuccessMessage('')}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Success! </strong>
                    {saveSuccessMessage}
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="ml-3"
                    onClick={() => navigate(`/balancesheet`)}
                  >
                    View in Balance Sheet &rarr;
                  </Button>
                </div>
              </Alert>
            )}

            {errorMessage && (
              <Alert variant="danger" dismissible onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            )}

            {/* Summary Metrics Cards */}
            <div className="row mb-3">
              <div className="col-md-3">
                <Card className="shadow-sm border-left-primary h-100 p-2 text-center" style={{ borderLeft: '4px solid #1a56db' }}>
                  <small className="text-muted font-weight-bold">TOTAL OWNER EQUITY</small>
                  <h4 className="font-weight-bold text-primary mb-0 mt-1">
                    <NumericFormat
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                      thousandSeparator
                      thousandsGroupStyle={thousandsdisplay || 'lakh'}
                      value={totalOwnerEquity}
                      prefix={`${curUnit} `}
                    />
                  </h4>
                  <small className="text-muted">Closing Partner Balance</small>
                </Card>
              </div>
              <div className="col-md-3">
                <Card className="shadow-sm border-left-info h-100 p-2 text-center" style={{ borderLeft: '4px solid #06b6d4' }}>
                  <small className="text-muted font-weight-bold">OPENING BALANCE</small>
                  <h5 className="font-weight-bold text-info mb-0 mt-1">
                    <NumericFormat
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                      thousandSeparator
                      thousandsGroupStyle={thousandsdisplay || 'lakh'}
                      value={totalOpening}
                      prefix={`${curUnit} `}
                    />
                  </h5>
                  <small className="text-muted">Opening balance at start of FY</small>
                </Card>
              </div>
              <div className="col-md-3">
                <Card className="shadow-sm border-left-success h-100 p-2 text-center" style={{ borderLeft: '4px solid #10b981' }}>
                  <small className="text-muted font-weight-bold">ADDITIONS / CONTRIBUTIONS</small>
                  <h5 className="font-weight-bold text-success mb-0 mt-1">
                    <NumericFormat
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                      thousandSeparator
                      thousandsGroupStyle={thousandsdisplay || 'lakh'}
                      value={totalAdditions}
                      prefix={`${curUnit} `}
                    />
                  </h5>
                  <small className="text-muted">Capital / funds introduced</small>
                </Card>
              </div>
              <div className="col-md-3">
                <Card className="shadow-sm border-left-danger h-100 p-2 text-center" style={{ borderLeft: '4px solid #ef4444' }}>
                  <small className="text-muted font-weight-bold">DRAWINGS / WITHDRAWALS</small>
                  <h5 className="font-weight-bold text-danger mb-0 mt-1">
                    <NumericFormat
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                      thousandSeparator
                      thousandsGroupStyle={thousandsdisplay || 'lakh'}
                      value={totalDrawings}
                      prefix={`${curUnit} `}
                    />
                  </h5>
                  <small className="text-muted">Drawings taken by partners</small>
                </Card>
              </div>
            </div>

            {/* Partners Entry Table */}
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
                <h6 className="font-weight-bold mb-0 text-dark">
                  Partner Current Allocations for Financial Year: <span className="text-primary">{financialYearId || 'None'}</span>
                </h6>
                <Button variant="outline-primary" size="sm" onClick={handleAddPartnerRow}>
                  <AddIcon style={{ fontSize: 18 }} className="mr-1" /> Add Partner
                </Button>
              </Card.Header>

              <Card.Body className="p-0">
                <form onSubmit={handleSave}>
                  <div className="table-responsive">
                    <Table className="table table-sm table-hover mb-0" style={{ minWidth: '850px' }}>
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '22%' }}>Partner / Shareholder</th>
                          <th style={{ width: '10%' }} className="text-center">Share (%)</th>
                          <th style={{ width: '14%' }} className="text-right">Opening ({curUnit})</th>
                          <th style={{ width: '14%' }} className="text-right">Additions (+)</th>
                          <th style={{ width: '14%' }} className="text-right">Drawings (-)</th>
                          <th style={{ width: '16%' }} className="text-right">Closing / Net Balance</th>
                          <th style={{ width: '6%' }} className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerRows.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-4 text-muted">
                              No partners found. Click "Add Partner" to enter partner current allocations.
                            </td>
                          </tr>
                        ) : (
                          partnerRows.map((row, index) => (
                            <tr key={row.partnerId || index}>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={row.partnerName}
                                  onChange={(e) => handleRowChange(index, 'partnerName', e.target.value)}
                                  placeholder="Partner Name"
                                  required
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm text-center"
                                  value={row.sharePercentage !== undefined ? row.sharePercentage : ''}
                                  onChange={(e) => handleRowChange(index, 'sharePercentage', e.target.value)}
                                  placeholder="0"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm text-right"
                                  value={row.openingBalance !== undefined ? row.openingBalance : ''}
                                  onChange={(e) => handleRowChange(index, 'openingBalance', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm text-right text-success"
                                  value={row.additions !== undefined ? row.additions : ''}
                                  onChange={(e) => handleRowChange(index, 'additions', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm text-right text-danger"
                                  value={row.drawings !== undefined ? row.drawings : ''}
                                  onChange={(e) => handleRowChange(index, 'drawings', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm text-right font-weight-bold"
                                  value={row.closingAmount !== undefined ? row.closingAmount : ''}
                                  onChange={(e) => handleRowChange(index, 'closingAmount', e.target.value)}
                                  placeholder="0.00"
                                  required
                                />
                              </td>
                              <td className="text-center align-middle">
                                <Tooltip title="Remove Partner Row">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemovePartnerRow(index)}
                                    disabled={partnerRows.length === 1}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      {partnerRows.length > 0 && (
                        <tfoot className="bg-light font-weight-bold">
                          <tr>
                            <td className="text-left font-weight-bold">Total</td>
                            <td className="text-center">
                              {partnerRows.reduce((s, r) => s + (Number(r.sharePercentage) || 0), 0).toFixed(1)}%
                            </td>
                            <td className="text-right">
                              <NumericFormat
                                displayType="text"
                                decimalScale={2}
                                fixedDecimalScale
                                thousandSeparator
                                thousandsGroupStyle={thousandsdisplay || 'lakh'}
                                value={totalOpening}
                              />
                            </td>
                            <td className="text-right text-success">
                              <NumericFormat
                                displayType="text"
                                decimalScale={2}
                                fixedDecimalScale
                                thousandSeparator
                                thousandsGroupStyle={thousandsdisplay || 'lakh'}
                                value={totalAdditions}
                              />
                            </td>
                            <td className="text-right text-danger">
                              <NumericFormat
                                displayType="text"
                                decimalScale={2}
                                fixedDecimalScale
                                thousandSeparator
                                thousandsGroupStyle={thousandsdisplay || 'lakh'}
                                value={totalDrawings}
                              />
                            </td>
                            <td className="text-right text-primary font-weight-bold" style={{ fontSize: '15px' }}>
                              <NumericFormat
                                displayType="text"
                                decimalScale={2}
                                fixedDecimalScale
                                thousandSeparator
                                thousandsGroupStyle={thousandsdisplay || 'lakh'}
                                value={totalOwnerEquity}
                              />
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </div>

                  <div className="p-3 bg-white border-top d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-muted">
                        <i className="fas fa-info-circle mr-1"></i>
                        Saving this entry stores the partner current allocations for FY {financialYearId} and will immediately reflect in the Balance Sheet under Owner Equity.
                      </small>
                    </div>
                    <div>
                      <Button
                        type="submit"
                        className="btn btn-primary px-4 font-weight-bold"
                        disabled={loading || !financialYearId || partnerRows.length === 0}
                      >
                        {loading ? 'Saving...' : 'Save Partner Current / Owner Equity'}
                      </Button>
                    </div>
                  </div>
                </form>
              </Card.Body>
            </Card>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default PartnerCurrentEquity;
